/**
 * MarkdownPreviewModal — opens a full-screen modal that renders a Phase 4
 * Markdown artifact inline (via react-markdown + remark-gfm).
 *
 * Used by Phase4Page report cards so reviewers can read any document inline
 * without forcing a download. PDF artifacts open in a new browser tab via
 * the `/api/phase4/view/{name}` endpoint (inline content-disposition).
 */
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Download, FileType, Loader2, AlertCircle } from 'lucide-react';

export default function MarkdownPreviewModal({ open, item, onClose }) {
  const [body, setBody] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open || !item) return;
    setBody(null);
    setError(null);
    setLoading(true);
    fetch(`/api/phase4/raw/${encodeURIComponent(item.name)}`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => setBody(d.markdown))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [open, item]);

  if (!open || !item) return null;

  // Find paired PDF for the "Open PDF in new tab" action
  const pdfName = item.pairs_with
    ? item.pairs_with.replace('.md', '.pdf')
    : item.name.replace('.md', '.pdf');

  return (
    <div
      className="fixed inset-0 z-[70] flex items-stretch justify-center p-4 sm:p-8 backdrop-blur-md bg-black/60"
      onClick={onClose}
      data-testid="md-preview-modal"
    >
      <div
        className="liquid-card max-w-4xl w-full max-h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-white/5 shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--ink-tertiary)] truncate">
              MSML · Phase 4 · Live preview
            </div>
            <h2
              className="text-base sm:text-lg font-semibold truncate"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="md-preview-title"
            >
              {item.label || item.name}
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap shrink-0">
            <a
              href={`/api/phase4/view/${encodeURIComponent(pdfName)}`}
              target="_blank"
              rel="noreferrer"
              className="liquid-btn text-xs"
              data-testid="md-preview-open-pdf"
            >
              <FileType className="w-3 h-3 mr-1.5 inline" />
              Open PDF
            </a>
            <a
              href={`/api/phase4/download/${encodeURIComponent(item.name)}`}
              className="liquid-btn text-xs"
              data-testid="md-preview-download-md"
            >
              <Download className="w-3 h-3 mr-1.5 inline" />
              Download MD
            </a>
            <button
              onClick={onClose}
              className="liquid-btn text-xs"
              aria-label="Close"
              data-testid="md-preview-close"
            >
              <X className="w-3 h-3 mr-1.5 inline" />
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-8">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[var(--ink-secondary)]" data-testid="md-preview-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 text-sm text-[var(--status-danger-text)]" data-testid="md-preview-error">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Could not load preview</div>
                <div className="text-xs opacity-80 mt-1">{error}</div>
              </div>
            </div>
          )}
          {body && (
            <article
              className="md-rendered text-[var(--ink-primary)]"
              data-testid="md-preview-body"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
            </article>
          )}
        </div>
      </div>

      {/* Inline CSS for prose styling (scoped) */}
      <style>{`
        .md-rendered { font-size: 14px; line-height: 1.65; }
        .md-rendered h1 { font-family: Outfit, sans-serif; font-size: 28px; color: var(--accent-cyan); margin: 0 0 0.4em; padding-bottom: 0.25em; border-bottom: 2px solid var(--accent-cyan); }
        .md-rendered h2 { font-family: Outfit, sans-serif; font-size: 20px; margin-top: 1.4em; margin-bottom: 0.4em; color: var(--ink-primary); }
        .md-rendered h3 { font-family: Outfit, sans-serif; font-size: 16px; margin-top: 1.1em; margin-bottom: 0.3em; color: var(--ink-primary); }
        .md-rendered p, .md-rendered ul, .md-rendered ol { margin: 0.6em 0; color: var(--ink-secondary); }
        .md-rendered strong { color: var(--ink-primary); }
        .md-rendered em { color: var(--ink-secondary); }
        .md-rendered code { font-family: ui-monospace, SF Mono, Menlo, monospace; background: rgba(255,255,255,0.05); padding: 1px 6px; border-radius: 4px; font-size: 0.92em; word-break: break-word; }
        .md-rendered pre { background: rgba(0,0,0,0.35); padding: 12px; border-radius: 8px; font-size: 0.9em; white-space: pre-wrap; word-break: break-word; border: 1px solid rgba(255,255,255,0.06); }
        .md-rendered table { border-collapse: collapse; margin: 1em 0; width: 100%; font-size: 0.92em; }
        .md-rendered th, .md-rendered td { border: 1px solid rgba(255,255,255,0.1); padding: 6px 10px; text-align: left; vertical-align: top; color: var(--ink-secondary); }
        .md-rendered th { background: rgba(255,255,255,0.04); color: var(--ink-primary); font-weight: 600; }
        .md-rendered tr:nth-child(even) td { background: rgba(255,255,255,0.02); }
        .md-rendered blockquote { border-left: 3px solid var(--accent-cyan); padding: 0.2em 0.9em; margin: 0.8em 0; color: var(--ink-secondary); background: rgba(44, 225, 233, 0.05); }
        .md-rendered hr { border: none; border-top: 1px solid rgba(255,255,255,0.08); margin: 1.2em 0; }
        .md-rendered a { color: var(--accent-cyan); text-decoration: none; }
        .md-rendered a:hover { text-decoration: underline; }
      `}</style>
    </div>
  );
}
